// NOT-542 — reusable Business Unit picker. Lists ACTIVE units, value is the
// businessUnitId (null = unassigned). Zero-cost when unused: renders nothing
// when the tenant has no active business units, so it never forces the concept
// onto tenants that don't use it.
//
// A value whose unit has since been ARCHIVED is still rendered, marked, and
// therefore clearable. Radix falls back to the placeholder for a value with no
// matching item, which reads as "nothing selected" while the id is still held in
// form state and still submitted on save. The extra item exists only while that
// id IS the value, so an archived unit can never be picked afresh.
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { resolveSelectedBusinessUnits, useBusinessUnitOptions } from './useBusinessUnitOptions';

const NONE_VALUE = 'none';

export interface BusinessUnitSelectProps {
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  disabled?: boolean;
  placeholder?: string;
  allowClear?: boolean;
  /**
   * Restrict the offered units to these ids — the Business Units of the
   * selected Job Type. Undefined or empty means "no restriction": a Job Type
   * that lists no units belongs to every one of them, and before a type is
   * chosen there is nothing to narrow by.
   */
  allowedIds?: readonly string[];
}

/** A small color chip + name row shown inside the select. */
function BusinessUnitOptionLabel({ color, name }: { color: string; name: string }) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span
        className="h-2.5 w-2.5 shrink-0 rounded-full border"
        style={{ backgroundColor: color }}
      />
      <span className="truncate">{name}</span>
    </span>
  );
}

export function BusinessUnitSelect({
  value,
  onChange,
  disabled,
  placeholder = 'No business unit',
  allowClear = true,
  allowedIds,
}: BusinessUnitSelectProps) {
  const { activeUnits, unitsById } = useBusinessUnitOptions();

  // Zero-cost when unused — hide entirely until the tenant defines a unit.
  // Keyed on ACTIVE units: an archived-only tenant still sees nothing.
  if (activeUnits.length === 0) return null;

  // Narrow to the Job Type's units when it names any. An empty/absent list is a
  // wildcard, not "nothing allowed" — narrowing to zero would strand the picker
  // with no choices at all.
  //
  // A value outside that list still renders: an edited job can carry a unit the
  // Job Type no longer lists, and dropping its item would make Radix fall back
  // to the placeholder — reading as "nothing selected" while the id is still in
  // form state and still submitted. Same rule as the archived case below.
  const offeredUnits =
    allowedIds && allowedIds.length > 0
      ? activeUnits.filter((unit) => allowedIds.includes(unit.id) || unit.id === value)
      : activeUnits;

  // Non-null only when the current value names a unit that can no longer be
  // chosen (archived, or gone) — the one case that needs its own item.
  const [current] = value ? resolveSelectedBusinessUnits([value], unitsById) : [];
  const stranded = current?.archived ? current : undefined;

  return (
    <Select
      value={value ?? (allowClear ? NONE_VALUE : '')}
      disabled={disabled}
      onValueChange={(next) => onChange(next === NONE_VALUE ? null : next)}
    >
      <SelectTrigger className="w-full">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {allowClear && <SelectItem value={NONE_VALUE}>{placeholder}</SelectItem>}
        {offeredUnits.map((unit) => (
          <SelectItem key={unit.id} value={unit.id}>
            <BusinessUnitOptionLabel color={unit.color} name={unit.name} />
          </SelectItem>
        ))}
        {stranded ? (
          <SelectItem value={stranded.id}>
            <BusinessUnitOptionLabel
              color={stranded.unit?.color ?? 'transparent'}
              name={stranded.unit ? `${stranded.label} (archived)` : stranded.label}
            />
          </SelectItem>
        ) : null}
      </SelectContent>
    </Select>
  );
}
